/* eslint-disable max-len */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Chip,
  Alert,
  Button,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  DeleteOutline as DeleteOutlineIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import HeightVolumeChart from './HeightVolumeChart';
import { buildBoundaryContext, detectHeightVolumeGaps } from './GapAnalysisUtils';

const PAGE_ACCENT_COLORS = [
  '#b8c0ff',
  '#cdb4db',
  '#a9def9',
  '#bde0fe',
  '#c8b6ff',
  '#bee3db',
  '#d7e3fc',
  '#d8e2dc',
];

const getPageAccentColor = (pageNumber, tableIndex) => (
  PAGE_ACCENT_COLORS[(pageNumber + tableIndex) % PAGE_ACCENT_COLORS.length]
);

const formatRawValue = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'number') {
    return Number.isNaN(value) ? '' : `${value}`;
  }

  return `${value}`;
};

const formatSignedNumber = (value, digits = 3) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${value > 0 ? '+' : ''}${value.toFixed(digits)}`;
};

const formatGapWarning = (gap) => {
  const details = [
    `Δ hauteur ${formatSignedNumber(gap.deltaHeight)} m`,
    gap.deltaVolume !== null ? `Δ volume ${formatSignedNumber(gap.deltaVolume)} m³` : null,
    gap.typicalHeightStep > 0 ? `pas habituel ${gap.typicalHeightStep.toFixed(3)} m` : null,
  ].filter(Boolean).join(', ');
  const possibleMissingHeights = gap.expectedMissingHeights.length > 0
    ? ` Hauteur(s) possible(s) à vérifier : ${gap.expectedMissingHeights.join(', ')} m.`
    : '';

  return `${gap.label} entre ${gap.startHeight} m et ${gap.endHeight} m (${details}).${possibleMissingHeights}`;
};

const generateDataPointId = () => `dp_${uuidv4()}`;

// Ensure every row has a stable identifier so downstream consumers can rely on it
const ensureDataPointIds = (existingIds, targetLength) => {
  const normalizedIds = [...(existingIds || [])];
  for (let i = 0; i < targetLength; i += 1) {
    if (!normalizedIds[i]) {
      normalizedIds[i] = generateDataPointId();
    }
  }
  return normalizedIds;
};

/**
 * Validates height-volume data for coherence
 * Checks for gaps in 3-by-3 patterns
 */
export const validateHeightVolumeCoherence = (heights, volumes, previousTable = null) => {
  const errors = [];
  const warnings = [];

  if (heights.length !== volumes.length) {
    errors.push('Les tableaux de hauteur et de volume doivent avoir la même longueur');
    return { errors, warnings, isValid: false };
  }

  if (heights.length < 3) {
    warnings.push('Au moins 3 points de données sont nécessaires pour une validation appropriée');
  }

  const boundaryContext = previousTable
    ? buildBoundaryContext(previousTable, {
      heights,
      volumes,
      pageNumber: previousTable.pageNumber !== undefined ? previousTable.pageNumber + 1 : undefined,
    })
    : null;
  const gapAnalysis = detectHeightVolumeGaps(heights, volumes, [], boundaryContext);

  if (
    previousTable
    && boundaryContext
  ) {
    const boundaryDiff = boundaryContext.currentHeight - boundaryContext.previousHeight;
    const boundaryGap = gapAnalysis.boundaryGaps[0];

    if (boundaryDiff < 0) {
      errors.push(`La première hauteur ${boundaryContext.currentHeight} est inférieure à la dernière hauteur de la page précédente ${boundaryContext.previousHeight}`);
    } else if (boundaryGap) {
      warnings.push(formatGapWarning(boundaryGap));
    }
  }

  gapAnalysis.gaps
    .filter((gap) => gap.kind === 'internal_gap')
    .forEach((gap) => {
      warnings.push(formatGapWarning(gap));
    });

  // Check for decreasing heights
  for (let i = 0; i < heights.length - 1; i += 1) {
    const currentHeight = heights[i];
    const nextHeight = heights[i + 1];
    const heightDiff = nextHeight - currentHeight;

    if (heightDiff < 0) {
      errors.push(`La hauteur diminue de ${currentHeight} à ${nextHeight}`);
    }
  }

  // Check volume progression (should generally increase with height)
  for (let i = 0; i < volumes.length - 1; i += 1) {
    const currentVolume = volumes[i];
    const nextVolume = volumes[i + 1];

    // Volume should generally increase with height
    if (heights[i] < heights[i + 1] && nextVolume < currentVolume) {
      warnings.push(`Le volume diminue alors que la hauteur augmente au point ${i + 1}`);
    }
  }

  // Check for 3-by-3 patterns
  for (let i = 0; i < heights.length - 2; i += 1) {
    const h1 = heights[i];
    const h2 = heights[i + 1];
    const h3 = heights[i + 2];
    const v1 = volumes[i];
    const v2 = volumes[i + 1];
    const v3 = volumes[i + 2];

    // Check if the pattern is reasonable
    const heightStep1 = h2 - h1;
    const heightStep2 = h3 - h2;
    const volumeStep1 = v2 - v1;
    const volumeStep2 = v3 - v2;

    // If height steps are similar, volume steps should be similar too
    if (Math.abs(heightStep1 - heightStep2) < 0.01 && Math.abs(volumeStep1 - volumeStep2) > Math.abs(volumeStep1) * 0.5) {
      warnings.push(`Progression de volume incohérente dans le motif à 3 points commençant à la hauteur ${h1}`);
    }
  }

  return {
    errors,
    warnings,
    isValid: errors.length === 0,
  };
};

export const VALIDATION_RULES_DESCRIPTION = `if len(heights) != len(volumes):
  error "incompatibilité de longueur hauteur/volume"

if len(heights) < 3:
  warn "au moins 3 points nécessaires pour la validation"

typicalHeightStep = median(positive height deltas inside the table)
typicalVolumeStep = median(positive volume deltas inside the table)

if previous table exists:
  compare previous final point with current first point
  if current height < previous height:
    error "la première hauteur est inférieure à la page précédente"
  if height delta or volume delta is > 1.5 * typical step:
    warn "écart entre pages ?" with delta details

for i = 0 .. n-2:
  heightDiff = heights[i+1] - heights[i]
  volumeDiff = volumes[i+1] - volumes[i]
  if heightDiff or volumeDiff is > 1.5 * typical step:
    warn "écart inhabituel" with delta details
  if heightDiff < 0:
    error "la hauteur diminue"

for i = 0 .. n-2:
  if heights[i] < heights[i+1] and volumes[i+1] < volumes[i]:
    warn "le volume diminue alors que la hauteur augmente"

for i = 0 .. n-3:
  stepH1 = heights[i+1] - heights[i]
  stepH2 = heights[i+2] - heights[i+1]
  stepV1 = volumes[i+1] - volumes[i]
  stepV2 = volumes[i+2] - volumes[i+1]
  if abs(stepH1 - stepH2) < 0.01 and abs(stepV1 - stepV2) > 0.5 * abs(stepV1):
    warn "progression de volume incohérente sur 3 points"`;

/**
 * Editable DataTable for height-volume data
 */
function EditableHeightVolumeTable({
  data,
  pageNumber,
  tableIndex,
  onDataChange,
  validation,
  previousTable,
}) {
  const [editingRow, setEditingRow] = useState(null);
  const [editingField, setEditingField] = useState(null); // 'height' or 'volume'
  const [editData, setEditData] = useState({ height: '', volume: '' });
  const [tableHeight, setTableHeight] = useState(500); // Increased default height
  const [isResizing, setIsResizing] = useState(false);
  const [deleteRowIndex, setDeleteRowIndex] = useState(null);

  const handleEdit = (index, field = null) => {
    setEditingRow(index);
    setEditingField(field);
    setEditData({
      height: data.heights[index] || '',
      volume: data.volumes[index] || '',
    });
  };

  const handleSave = () => {
    if (editingRow !== null) {
      const newHeights = [...data.heights];
      const newVolumes = [...data.volumes];

      newHeights[editingRow] = parseFloat(editData.height) || 0;
      newVolumes[editingRow] = parseFloat(editData.volume) || 0;

      onDataChange({
        ...data,
        heights: newHeights,
        volumes: newVolumes,
      });

      setEditingRow(null);
      setEditData({ height: '', volume: '' });
    }
  };

  const handleCancel = () => {
    setEditingRow(null);
    setEditingField(null);
    setEditData({ height: '', volume: '' });
  };

  const handleAddRow = () => {
    const newHeights = [...data.heights, 0];
    const newVolumes = [...data.volumes, 0];
    const baseIds = ensureDataPointIds(data.dataPointIds, data.heights.length);
    const newDataPointIds = [...baseIds, generateDataPointId()];

    onDataChange({
      ...data,
      heights: newHeights,
      volumes: newVolumes,
      dataPointIds: newDataPointIds,
    });
  };

  const handleDeleteRow = (index) => {
    if (data.heights.length <= 1) return;
    setDeleteRowIndex(index);
  };

  const handleConfirmDeleteRow = () => {
    if (deleteRowIndex === null || data.heights.length <= 1) return;

    const normalizedIds = ensureDataPointIds(data.dataPointIds, data.heights.length);
    const newHeights = data.heights.filter((_, i) => i !== deleteRowIndex);
    const newVolumes = data.volumes.filter((_, i) => i !== deleteRowIndex);
    const newDataPointIds = normalizedIds.filter((_, i) => i !== deleteRowIndex);

    onDataChange({
      ...data,
      heights: newHeights,
      volumes: newVolumes,
      dataPointIds: newDataPointIds,
    });
    setDeleteRowIndex(null);
  };

  const handleInsertRow = (index, position = 'below') => {
    const insertIndex = position === 'above' ? index : index + 1;
    const newHeights = [...data.heights];
    const newVolumes = [...data.volumes];
    newHeights.splice(insertIndex, 0, 0);
    newVolumes.splice(insertIndex, 0, 0);

    const baseIds = ensureDataPointIds(data.dataPointIds, data.heights.length);
    const newDataPointIds = [...baseIds];
    newDataPointIds.splice(insertIndex, 0, generateDataPointId());

    onDataChange({
      ...data,
      heights: newHeights,
      volumes: newVolumes,
      dataPointIds: newDataPointIds,
    });
  };

  const handleMoveRow = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= data.heights.length) return;

    const normalizedIds = ensureDataPointIds(data.dataPointIds, data.heights.length);
    const newHeights = [...data.heights];
    const newVolumes = [...data.volumes];
    const newDataPointIds = [...normalizedIds];

    [newHeights[index], newHeights[targetIndex]] = [newHeights[targetIndex], newHeights[index]];
    [newVolumes[index], newVolumes[targetIndex]] = [newVolumes[targetIndex], newVolumes[index]];
    [newDataPointIds[index], newDataPointIds[targetIndex]] = [
      newDataPointIds[targetIndex],
      newDataPointIds[index],
    ];

    onDataChange({
      ...data,
      heights: newHeights,
      volumes: newVolumes,
      dataPointIds: newDataPointIds,
    });
  };

  const handleMouseDown = (e) => {
    setIsResizing(true);
    e.preventDefault();
  };

  const handleMouseMove = useCallback((e) => {
    if (!isResizing) return;

    const tableContainer = document.querySelector(`[data-table-container="${pageNumber}-${tableIndex}"]`);
    if (!tableContainer) return;

    const rect = tableContainer.getBoundingClientRect();
    const newHeight = e.clientY - rect.top;
    const minHeight = 200;
    const maxHeight = 800;

    if (newHeight >= minHeight && newHeight <= maxHeight) {
      setTableHeight(newHeight);
    }
  }, [isResizing, pageNumber, tableIndex]);

  const handleMouseUp = () => {
    setIsResizing(false);
  };

  // Add event listeners for mouse events
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
    return undefined;
  }, [isResizing, handleMouseMove]);

  const errorKeyCounts = {};
  const warningKeyCounts = {};
  const previousBoundary = buildBoundaryContext(previousTable, data);
  const gapAnalysis = detectHeightVolumeGaps(
    data.heights,
    data.volumes,
    data.heights.map(() => data.pageNumber),
    previousBoundary,
  );
  const boundaryGap = gapAnalysis.boundaryGaps[0];
  const boundaryHeightDelta = previousBoundary
    ? previousBoundary.currentHeight - previousBoundary.previousHeight
    : null;
  const boundaryVolumeDelta = previousBoundary
    ? previousBoundary.currentVolume - previousBoundary.previousVolume
    : null;

  return (
    <Box>
      <Box sx={{ position: 'relative' }}>
        <TableContainer
          data-table-container={`${pageNumber}-${tableIndex}`}
          sx={{
            height: tableHeight,
            overflow: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Hauteur (m)</TableCell>
                <TableCell>Volume (m³)</TableCell>
                <TableCell>Δ Volume (m³)</TableCell>
                <TableCell>% Δ vs précédent</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {previousBoundary && (
                <TableRow
                  sx={{
                    backgroundColor: boundaryGap
                      ? 'rgba(255, 152, 0, 0.16)'
                      : 'rgba(0, 0, 0, 0.035)',
                  }}
                >
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      Page précédente
                      {' '}
                      {formatRawValue(previousBoundary.previousHeight)}
                      {' '}
                      →
                      {' '}
                      {formatRawValue(previousBoundary.currentHeight)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {formatRawValue(previousBoundary.previousVolume)}
                      {' '}
                      →
                      {' '}
                      {formatRawValue(previousBoundary.currentVolume)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 120 }}>
                    {formatSignedNumber(boundaryVolumeDelta)}
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    <Chip
                      size="small"
                      color={boundaryGap ? 'warning' : 'default'}
                      label={`Δ hauteur ${formatSignedNumber(boundaryHeightDelta)} m`}
                    />
                    {boundaryGap?.expectedMissingHeights.length > 0 && (
                      <Typography variant="caption" sx={{ color: 'warning.dark', display: 'block', mt: 0.5 }}>
                        À vérifier :
                        {' '}
                        {boundaryGap.expectedMissingHeights.join(', ')}
                        {' '}
                        m
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="caption" color="text.secondary">
                      Jonction
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {data.heights.map((height, index) => {
                const fallbackId = `dp_${pageNumber}_${tableIndex}_${index}_${String(height)}_${String(data.volumes[index])}`;
                const dataPointId = data.dataPointIds?.[index] || fallbackId;
                const previousHeight = index > 0 ? data.heights[index - 1] : null;
                const previousVolume = index > 0 ? data.volumes[index - 1] : null;
                const previousHeightDelta = index > 1 ? data.heights[index - 1] - data.heights[index - 2] : null;
                const previousVolumeDelta = index > 1 ? data.volumes[index - 1] - data.volumes[index - 2] : null;

                const heightDelta = previousHeight !== null ? height - previousHeight : null;
                const volumeDelta = previousVolume !== null ? data.volumes[index] - previousVolume : null;
                const gapDifference = (volumeDelta !== null && previousVolumeDelta !== null)
                  ? volumeDelta - previousVolumeDelta
                  : null;
                const gapDifferencePercent = (gapDifference !== null
                  && previousVolumeDelta !== null
                  && Math.abs(previousVolumeDelta) > 1e-6)
                  ? (gapDifference / previousVolumeDelta) * 100
                  : null;
                const rowGap = gapAnalysis.gaps.find((gap) => (
                  gap.kind === 'internal_gap' && gap.segmentIndex === index - 1
                ));
                const gapDifferenceLabel = gapDifferencePercent !== null && index > 1
                  ? `${gapDifferencePercent >= 0 ? '+' : ''}${gapDifferencePercent.toFixed(1)}%`
                  : '—';

                let rowSeverity = 'none';
                if (heightDelta !== null && heightDelta < -0.001) {
                  rowSeverity = 'error';
                } else if (volumeDelta !== null && volumeDelta < -0.001) {
                  rowSeverity = 'error';
                } else if (rowGap) {
                  rowSeverity = 'warning';
                } else if (gapDifferencePercent !== null) {
                  const absPercent = Math.abs(gapDifferencePercent);
                  if (absPercent >= 50) {
                    rowSeverity = 'error';
                  } else if (absPercent >= 20) {
                    rowSeverity = 'warning';
                  }
                } else if (gapDifference !== null && previousVolumeDelta === 0 && Math.abs(gapDifference) > 0.05) {
                  rowSeverity = 'warning';
                } else if (heightDelta !== null && previousHeightDelta !== null) {
                  const relativeHeightChange = Math.abs(heightDelta - previousHeightDelta);
                  if (Math.abs(previousHeightDelta) > 1e-6 && relativeHeightChange / Math.abs(previousHeightDelta) > 0.5) {
                    rowSeverity = 'warning';
                  }
                }

                const baseBackground = index % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.04)';
                const severityBackground = {
                  none: baseBackground,
                  warning: 'rgba(255, 152, 0, 0.16)',
                  error: 'rgba(211, 47, 47, 0.18)',
                }[rowSeverity];
                const hoverBackground = {
                  none: 'rgba(0, 0, 0, 0.08)',
                  warning: 'rgba(255, 152, 0, 0.26)',
                  error: 'rgba(211, 47, 47, 0.28)',
                }[rowSeverity];

                return (
                  <TableRow
                    key={dataPointId}
                    sx={{
                      backgroundColor: severityBackground,
                      transition: 'background-color 0.2s ease',
                      '&:hover': {
                        backgroundColor: hoverBackground,
                      },
                    }}
                  >
                    <TableCell
                      onDoubleClick={() => handleEdit(index, 'height')}
                      sx={{ cursor: 'pointer' }}
                    >
                      {editingRow === index ? (
                        <TextField
                          size="small"
                          type="number"
                          value={editData.height}
                          onChange={(e) => setEditData({ ...editData, height: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleSave();
                            } else if (e.key === 'Escape') {
                              handleCancel();
                            } else if (e.key === 'Tab') {
                              // Allow tab navigation between fields
                              e.preventDefault();
                              const nextField = e.target.parentElement.parentElement.nextElementSibling?.querySelector('input');
                              if (nextField) {
                                nextField.focus();
                              }
                            }
                          }}
                          onBlur={(e) => {
                            // Only save on blur if we're not moving to the next field
                            if (!e.relatedTarget || !e.relatedTarget.closest('input')) {
                              handleSave();
                            }
                          }}
                          autoFocus={editingField === 'height'}
                          inputProps={{ step: '0.01' }}
                        />
                      ) : (
                        formatRawValue(height)
                      )}
                    </TableCell>
                    <TableCell
                      onDoubleClick={() => handleEdit(index, 'volume')}
                      sx={{ cursor: 'pointer' }}
                    >
                      {editingRow === index ? (
                        <TextField
                          size="small"
                          type="number"
                          value={editData.volume}
                          onChange={(e) => setEditData({ ...editData, volume: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleSave();
                            } else if (e.key === 'Escape') {
                              handleCancel();
                            } else if (e.key === 'Tab') {
                              // Allow tab navigation between fields
                              e.preventDefault();
                              const prevField = e.target.parentElement.parentElement.previousElementSibling?.querySelector('input');
                              if (prevField) {
                                prevField.focus();
                              }
                            }
                          }}
                          onBlur={(e) => {
                            // Only save on blur if we're not moving to the previous field
                            if (!e.relatedTarget || !e.relatedTarget.closest('input')) {
                              handleSave();
                            }
                          }}
                          autoFocus={editingField === 'volume'}
                          inputProps={{ step: '0.01' }}
                        />
                      ) : (
                        formatRawValue(data.volumes[index])
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      {volumeDelta !== null && index > 0
                        ? `${volumeDelta > 0 ? '+' : ''}${volumeDelta.toFixed(3)}`
                        : '—'}
                    </TableCell>
                    <TableCell sx={{ minWidth: 150 }}>
                      {rowGap ? (
                        <Box>
                          <Chip size="small" color="warning" label={rowGap.label} />
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                            Δ hauteur
                            {' '}
                            {formatSignedNumber(rowGap.deltaHeight)}
                            {' '}
                            m
                          </Typography>
                        </Box>
                      ) : (
                        gapDifferenceLabel
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.25 }}>
                        <Tooltip title="Insérer au-dessus">
                          <IconButton
                            size="small"
                            aria-label="Insérer une ligne au-dessus"
                            onClick={() => handleInsertRow(index, 'above')}
                          >
                            <AddCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Insérer en dessous">
                          <IconButton
                            size="small"
                            aria-label="Insérer une ligne en dessous"
                            onClick={() => handleInsertRow(index, 'below')}
                          >
                            <AddCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Monter">
                          <span>
                            <IconButton
                              size="small"
                              aria-label="Monter la ligne"
                              disabled={index === 0}
                              onClick={() => handleMoveRow(index, -1)}
                            >
                              <KeyboardArrowUpIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Descendre">
                          <span>
                            <IconButton
                              size="small"
                              aria-label="Descendre la ligne"
                              disabled={index === data.heights.length - 1}
                              onClick={() => handleMoveRow(index, 1)}
                            >
                              <KeyboardArrowDownIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <span>
                            <IconButton
                              size="small"
                              aria-label="Supprimer la ligne"
                              disabled={data.heights.length <= 1}
                              onClick={() => handleDeleteRow(index)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Resize handle */}
        <Box
          onMouseDown={handleMouseDown}
          sx={{
            position: 'absolute',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '60px',
            height: isResizing ? '12px' : '8px',
            cursor: 'ns-resize',
            backgroundColor: isResizing ? 'primary.main' : 'rgba(0, 0, 0, 0.15)',
            borderRadius: '4px 4px 0 0',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: isResizing ? 'primary.main' : 'rgba(0, 0, 0, 0.25)',
              height: '12px',
              width: '80px',
            },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&::before': {
              content: '""',
              width: '30px',
              height: '3px',
              backgroundColor: isResizing ? 'white' : 'rgba(0, 0, 0, 0.4)',
              borderRadius: '2px',
            },
            '&::after': {
              content: '""',
              width: '30px',
              height: '3px',
              backgroundColor: isResizing ? 'white' : 'rgba(0, 0, 0, 0.4)',
              borderRadius: '2px',
            },
          }}
        />
      </Box>

      <Box sx={{
        display: 'flex',
        gap: 1,
        mt: 1,
        mb: 2,
        alignItems: 'center',
      }}
      >
        <Button size="small" onClick={handleAddRow}>
          Ajouter une ligne
        </Button>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          Double-clic pour modifier • Actions visibles par ligne • Glisser le bas du tableau pour redimensionner
        </Typography>
        <Chip
          icon={validation.isValid ? <CheckCircleIcon /> : <WarningIcon />}
          label={validation.isValid ? 'Valide' : 'Problèmes détectés'}
          color={validation.isValid ? 'success' : 'warning'}
          size="small"
        />
      </Box>

      {/* Validation Messages */}
      {validation.errors.length > 0 && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {validation.errors.map((error) => {
            const occurrence = errorKeyCounts[error] || 0;
            errorKeyCounts[error] = occurrence + 1;
            const key = `error-${pageNumber}-${tableIndex}-${String(error)}-${occurrence}`;
            return (
              <Typography key={key} variant="body2">
                •
                {' '}
                {error}
              </Typography>
            );
          })}
        </Alert>
      )}

      {validation.warnings.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {validation.warnings.map((warning) => {
            const occurrence = warningKeyCounts[warning] || 0;
            warningKeyCounts[warning] = occurrence + 1;
            const key = `warning-${pageNumber}-${tableIndex}-${String(warning)}-${occurrence}`;
            return (
              <Typography key={key} variant="body2">
                •
                {' '}
                {warning}
              </Typography>
            );
          })}
        </Alert>
      )}

      <Dialog
        open={deleteRowIndex !== null}
        onClose={() => setDeleteRowIndex(null)}
        aria-labelledby={`delete-row-dialog-${pageNumber}-${tableIndex}`}
      >
        <DialogTitle id={`delete-row-dialog-${pageNumber}-${tableIndex}`}>
          Supprimer cette ligne ?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Cette correction est locale à cette page de vérification. Elle sera utilisée pour l&apos;export CSV,
            mais ne sera pas sauvegardée dans Firestore.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRowIndex(null)}>
            Annuler
          </Button>
          <Button color="error" variant="contained" onClick={handleConfirmDeleteRow}>
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/**
 * Main component for per-page table parsing and editing
 */
export default function PerPageTableParser({
  pageTables,
  onDataChange,
  onJumpToPage,
}) {
  const [validations, setValidations] = useState({});

  const buildValidations = useCallback((tables) => {
    const newValidations = {};
    tables.forEach((table, index) => {
      const previousTable = index > 0 ? tables[index - 1] : null;
      const validation = validateHeightVolumeCoherence(
        table.heights,
        table.volumes,
        previousTable,
      );
      newValidations[index] = validation;
    });
    return newValidations;
  }, []);

  // Validate page tables when they change
  useEffect(() => {
    setValidations(buildValidations(pageTables));
  }, [pageTables, buildValidations]);

  const handleTableDataChange = (index, newData) => {
    const newTables = [...pageTables];
    newTables[index] = newData;

    // Update validation, including page-boundary checks for following pages.
    setValidations(buildValidations(newTables));

    // Notify parent component
    onDataChange?.(newTables);
  };

  if (pageTables.length === 0) {
    return (
      <Alert severity="info">
        Aucun tableau trouvé dans le PDF. Vérifiez si le PDF contient des tableaux hauteur-volume.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Individual Tables */}
      {pageTables.map((table, index) => {
        const accentColor = getPageAccentColor(table.pageNumber, table.tableIndex);
        const previousTable = index > 0 ? pageTables[index - 1] : null;
        const boundaryContext = buildBoundaryContext(previousTable, table);
        return (
          <Card
            key={table.id || `table-${table.pageNumber}-${table.tableIndex}-${index}`}
            sx={{
              mb: 2,
              borderLeft: `8px solid ${accentColor}`,
              overflow: 'hidden',
            }}
          >
            <CardHeader
              title={`Page ${table.pageNumber + 1} - Tableau ${table.tableIndex + 1}`}
              sx={{
                '& .MuiCardHeader-title': {
                  fontWeight: 700,
                },
              }}
              action={
                onJumpToPage && (
                  <Button
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={() => onJumpToPage(table.pageNumber + 1)}
                    variant="outlined"
                    sx={{ borderColor: accentColor, color: accentColor }}
                  >
                    Voir la page
                  </Button>
                )
              }
            />
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Editable Table */}
                <EditableHeightVolumeTable
                  data={table}
                  pageNumber={table.pageNumber}
                  tableIndex={table.tableIndex}
                  onDataChange={(newData) => handleTableDataChange(index, newData)}
                  validation={validations[index] || { errors: [], warnings: [], isValid: true }}
                  previousTable={previousTable}
                />

                {/* Chart Visualization */}
                <HeightVolumeChart
                  heights={table.heights}
                  volumes={table.volumes}
                  pointPageNumbers={table.heights.map(() => table.pageNumber)}
                  boundaryContext={boundaryContext}
                  compartmentId={table.compartmentId}
                  title={`Page ${table.pageNumber + 1} - Tableau ${table.tableIndex + 1}`}
                />
              </Box>
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
}
